import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, User, Building2, Mail, Phone, Tag, Loader2, CheckCircle2, Sparkles, ScanLine } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { API } from "../lib/site";

const SCAN_STEPS = [
  "Fetching Google Maps metadata…",
  "Analyzing reviews & photos…",
  "Reading services & opening hours…",
  "Generating layout draft…",
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 pl-11 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-cyan-400/60 focus:bg-cyan-400/5 focus:shadow-[0_0_20px_rgba(0,240,255,0.12)] transition-[border-color,background-color,box-shadow] duration-300";

const Field = ({ icon: Icon, label, optional, children, testId }) => (
  <div data-testid={testId}>
    <label className="mb-2 flex items-center gap-2 text-xs font-mono font-semibold tracking-widest uppercase text-slate-400">
      {label}
      {optional && <span className="text-slate-600 normal-case tracking-normal">(optional)</span>}
    </label>
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 pointer-events-none" />
      {children}
    </div>
  </div>
);

export default function PreviewForm() {
  const [form, setForm] = useState({
    google_business_url: "",
    name: "",
    business_name: "",
    email: "",
    phone: "",
    business_type: "",
  });
  const [scanStep, setScanStep] = useState(-1);
  const [scanDone, setScanDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const scanTimers = useRef([]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const triggerScan = (value) => {
    scanTimers.current.forEach(clearTimeout);
    if (!value || value.length < 8 || !(value.includes("google") || value.includes("g.co") || value.includes("maps"))) {
      setScanStep(-1);
      setScanDone(false);
      return;
    }
    setScanDone(false);
    setScanStep(0);
    SCAN_STEPS.forEach((_, i) => {
      scanTimers.current.push(
        setTimeout(() => {
          if (i === SCAN_STEPS.length - 1) {
            setScanStep(-1);
            setScanDone(true);
          } else {
            setScanStep(i + 1);
          }
        }, (i + 1) * 900)
      );
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads/website-preview`, {
        ...form,
        business_type: form.business_type || null,
      });
      setSubmitted(res.data);
      toast.success("Preview request received — we'll build your draft within 48 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="preview" data-testid="website-preview-form-section" className="relative py-24 md:py-36 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="absolute -top-20 right-0 h-[420px] w-[420px] rounded-full bg-cyan-500/8 blur-[140px] pointer-events-none" />
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
        <div className="lg:sticky lg:top-28">
          <SectionHeading
            testId="preview-form-heading"
            kicker="Free website preview"
            title="See your new website before you pay a cent"
            sub="Paste your Google Business Profile link. Our AI drafts a complete, conversion-ready website for your business — delivered to your inbox within 48 hours, free."
          />
          <Reveal delay={0.2}>
            <ul className="mt-8 space-y-3.5">
              {["No credit card, no commitment", "Built from your real Google reviews & photos", "Keep it only if you love it"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm text-slate-300">
                  <Sparkles className="h-4 w-4 text-cyan-400 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="glow-card rounded-2xl p-7 sm:p-10">
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="text-center py-10"
                  data-testid="preview-form-success"
                >
                  <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-400/10 ring-1 ring-emerald-400/40">
                    <CheckCircle2 className="h-10 w-10 text-emerald-400" />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-semibold text-slate-50">
                    Your preview is in the queue
                  </h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-sm mx-auto">
                    Our AI is already reading {form.business_name || "your business"}'s Google profile.
                    We'll email your live draft to <span className="text-cyan-300">{form.email}</span> within 48 hours.
                  </p>
                  <p className="mt-5 font-mono text-xs text-slate-500">
                    REF: <span className="text-cyan-400">{submitted.id.slice(0, 8).toUpperCase()}</span>
                  </p>
                  <button
                    data-testid="preview-form-submit-another"
                    onClick={() => {
                      setSubmitted(null);
                      setForm({ google_business_url: "", name: "", business_name: "", email: "", phone: "", business_type: "" });
                    }}
                    className="mt-8 rounded-full border border-white/15 px-6 py-2.5 text-sm font-semibold text-slate-300 hover:border-cyan-400/50 hover:text-cyan-300 transition-colors"
                  >
                    Submit another business
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onSubmit={submit}
                  className="space-y-5"
                  data-testid="website-preview-form"
                >
                  <Field icon={Globe} label="Google Business Profile link" testId="preview-field-url">
                    <input
                      data-testid="website-preview-input-url"
                      type="url"
                      required
                      value={form.google_business_url}
                      onChange={(e) => {
                        set("google_business_url")(e);
                        triggerScan(e.target.value);
                      }}
                      placeholder="https://maps.google.com/your-business"
                      className={inputCls}
                    />
                  </Field>

                  <AnimatePresence>
                    {(scanStep >= 0 || scanDone) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <div
                          data-testid="preview-scan-status"
                          className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
                            scanDone ? "border-emerald-400/30 bg-emerald-400/5" : "border-cyan-400/25 bg-cyan-400/5"
                          }`}
                        >
                          {scanDone ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                              <span className="text-xs font-mono text-emerald-300">
                                Business profile detected — AI draft ready to generate
                              </span>
                            </>
                          ) : (
                            <>
                              <ScanLine className="h-4 w-4 text-cyan-400 shrink-0 scan-dot" />
                              <span className="text-xs font-mono text-cyan-300">{SCAN_STEPS[scanStep]}</span>
                            </>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field icon={User} label="Your name" testId="preview-field-name">
                      <input data-testid="website-preview-input-name" type="text" required value={form.name} onChange={set("name")} placeholder="Alex Rivera" className={inputCls} />
                    </Field>
                    <Field icon={Building2} label="Business name" testId="preview-field-business">
                      <input data-testid="website-preview-input-business" type="text" required value={form.business_name} onChange={set("business_name")} placeholder="Rivera Dental Studio" className={inputCls} />
                    </Field>
                    <Field icon={Mail} label="Email address" testId="preview-field-email">
                      <input data-testid="website-preview-input-email" type="email" required value={form.email} onChange={set("email")} placeholder="alex@riveradental.com" className={inputCls} />
                    </Field>
                    <Field icon={Phone} label="Phone number" testId="preview-field-phone">
                      <input data-testid="website-preview-input-phone" type="tel" required value={form.phone} onChange={set("phone")} placeholder="+1 (555) 010-2233" className={inputCls} />
                    </Field>
                  </div>

                  <Field icon={Tag} label="Business type" optional testId="preview-field-type">
                    <select
                      data-testid="website-preview-input-type"
                      value={form.business_type}
                      onChange={set("business_type")}
                      className={`${inputCls} appearance-none cursor-pointer`}
                    >
                      <option value="">Select your industry…</option>
                      {["Dental / Medical", "Restaurant / Café", "Salon / Spa", "Plumbing / HVAC", "Legal / Accounting", "Retail / Boutique", "Fitness / Gym", "Other local business"].map((t) => (
                        <option key={t} value={t} className="bg-[#0D111A]">{t}</option>
                      ))}
                    </select>
                  </Field>

                  <button
                    data-testid="website-preview-submit-button"
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2.5 rounded-xl bg-cyan-400 px-6 py-4 text-base font-bold text-[#07090E] hover:bg-cyan-300 hover:shadow-[0_0_40px_rgba(0,240,255,0.4)] disabled:opacity-60 transition-[background-color,box-shadow] duration-300"
                  >
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
                    {submitting ? "Sending to our AI…" : "Generate my free preview"}
                  </button>
                  <p className="text-center text-xs text-slate-500">
                    Free forever. Your data is only used to build your preview.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
